/**
 * Zod schemas for V4 View System types.
 *
 * Schemas are the single source of truth — TypeScript types are derived
 * via `z.infer<>`. Registry-derived union types (Intent, ArtifactCard, etc.)
 * use z.string() in schemas; the narrow unions are applied as type overrides
 * on the inferred types.
 */

import { z } from 'zod';
import type { DataType, Intent, ArtifactCard, GroupPresetId, ViewContext } from './registries';
import { JsonValueSchema } from '../schemas/json-value';

// ─────────────────────────────────────────────────────────────
// DisplayField
// ─────────────────────────────────────────────────────────────

export const DisplayFieldTypeSchema = z.union([
  z.literal('text'),
  z.literal('textarea'),
  z.literal('number'),
  z.literal('currency'),
  z.literal('percentage'),
  z.literal('date'),
  z.literal('boolean'),
  z.literal('markdown'),
  z.literal('email'),
  z.literal('select'),
  z.literal('html'),
  z.literal('badge'),
  z.literal('link'),
  z.literal('structured'),
]);
export type DisplayFieldType = z.infer<typeof DisplayFieldTypeSchema>;

/**
 * Payload schemas a `'structured'` field can carry. One entry per typed,
 * object-shaped artifact payload Terra renders with its own component; the
 * frontend keeps a matching renderer registry keyed by the same literal
 * (`web/app-home/src/view-system/structured/registry.tsx`). Adding a payload
 * means adding a key here and a renderer there — not a new DisplayFieldType.
 * (Blake, #1651 review: "a second typed payload will want a generic
 * structured type keyed by schema, not another literal".)
 */
export const StructuredSchemaKeySchema = z.enum(['deal_brief']);
export type StructuredSchemaKey = z.infer<typeof StructuredSchemaKeySchema>;

/**
 * Blueprint `detail.sections[]` entry. `renderer` accepts the full
 * DisplayFieldType set — the runtime rendering pipeline (buildDetailFields
 * in view-item-builders.ts, DisplayValue.tsx) already supports all of these,
 * so the request-validation schema must not be narrower than the runtime.
 * Single source of truth: the admin write route and the two skill tools all
 * import this instead of hand-rolling a narrow `z.enum(['markdown','text','html'])`.
 */
export const DetailSectionSchema = z
  .object({
    key: z.string().min(1),
    label: z.string().min(1),
    renderer: DisplayFieldTypeSchema.optional().describe(
      'How the field value is displayed. Defaults to text. Value-shape contracts: ' +
      "'link' expects the URL as the field value (display text comes from label); " +
      "'select'/'boolean' expect an enum/boolean value. For rich content prefer " +
      "'markdown'; 'html' is DOMPurify-sanitized at render. " +
      "'structured' expects an object payload (JSON-stringified in transit) validated and rendered by the component registered for `schema`; API-only.",
    ),
    schema: StructuredSchemaKeySchema.optional().describe("Required when renderer is 'structured': which payload schema/renderer the value belongs to."),
  })
  .superRefine((section, ctx) => {
    if (section.renderer === 'structured' && section.schema === undefined) {
      ctx.addIssue({ code: 'custom', path: ['schema'], message: "renderer 'structured' requires `schema`" });
    }
  });
export type DetailSection = z.infer<typeof DetailSectionSchema>;

export const DetailSchema = z.object({
  sections: z.array(DetailSectionSchema),
});
export type Detail = z.infer<typeof DetailSchema>;

// DisplayField.value is always a primitive. Link fields put the URL in `value`
// and the display text in the outer `label` field. Producers that receive
// arbitrary JSON must pass it through coerceDisplayFieldValue() before assigning.
export const DisplayFieldValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);
export type DisplayFieldValue = z.infer<typeof DisplayFieldValueSchema>;

export const DisplayFieldSchema = z.object({
  slot: z.string(),
  label: z.string().optional(),
  value: DisplayFieldValueSchema,
  type: DisplayFieldTypeSchema,
  /** `type === 'structured'` only: which registered payload renderer parses `value`. */
  schema: StructuredSchemaKeySchema.optional(),
  currentValue: DisplayFieldValueSchema.optional(),
  provenance: z.enum(['mapped', 'system']).optional(),
});
export type DisplayField = z.infer<typeof DisplayFieldSchema>;

/**
 * Coerce arbitrary blueprint/JSON data into the DisplayField value primitive.
 * Preserves primitives; extracts `.url` from legacy `{url, text?}` link objects
 * (the URL is what `value` represents — display text belongs in the outer
 * `label` field); falls back to JSON.stringify for other objects so they
 * render as a readable string instead of `[object Object]`.
 */
export function coerceDisplayFieldValue(
  value: unknown,
  type: DisplayFieldType,
): DisplayFieldValue {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (type === 'link' && typeof value === 'object' && 'url' in value) {
    const url = (value as { url: unknown }).url;
    return typeof url === 'string' ? url : String(url);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '[Object]';
  }
}

// ─────────────────────────────────────────────────────────────
// CardSlotValue
// ─────────────────────────────────────────────────────────────

export const CardSlotValueSchema = z.union([
  z.string(),
  z.object({ key: z.string(), type: DisplayFieldTypeSchema, schema: StructuredSchemaKeySchema.optional() }),
]);
export type CardSlotValue = z.infer<typeof CardSlotValueSchema>;

/** Blueprint `card` column shape: slot name → CardSlotValue (plain key or typed ref). */
export const CardMapSchema = z.record(z.string(), CardSlotValueSchema);
export type CardMap = z.infer<typeof CardMapSchema>;

/** Extract the data key from a CardSlotValue (plain string or {key, type} object). */
export function slotDataKey(val: CardSlotValue): string {
  return typeof val === 'string' ? val : val.key;
}

// ─────────────────────────────────────────────────────────────
// FormField
// ─────────────────────────────────────────────────────────────

/**
 * A form field the user fills in — the shape `review_single` sends. Only a single value
 * is sent per input field in the form.
 */
export const SingleFormFieldSchema = z.object({
  label: z.string(),
  type: z.string(), // DataType — registry-derived
  required: z.boolean().optional(),
  options: z.array(z.string()).optional(),
  value: JsonValueSchema.optional(),
  apiName: z.string().optional(),
  maxLength: z.number().optional(),
  /**
   * Display position, ascending. Fields without one keep their existing relative
   * order, after any that declare one.
   *
   * Needed because a blueprint's form is a JSON OBJECT and Postgres jsonb does not
   * preserve key insertion order — it returns keys sorted by length then
   * alphabetically. So `{draft_subject, draft_message}` comes back message-first,
   * and a form authored subject-first renders backwards with no way to fix it from
   * the blueprint alone.
   */
  order: z.number().optional(),
});
export type SingleFormField = Omit<z.infer<typeof SingleFormFieldSchema>, 'type'> & {
  /** DataType — narrowed from z.string() to registry union */
  type: DataType;
};

/**
 * A form field that can carry a before/after pair — the shape `review` sends.
 *
 * Extends the single shape rather than the reverse, so `currentValue` and
 * `proposedValue` are only ever declared where they mean something. A SingleFormField is
 * assignable to this, so helpers that flatten across modes (getModalForm) keep one
 * return type and every existing consumer is untouched.
 */
export const FormFieldSchema = SingleFormFieldSchema.extend({
  currentValue: JsonValueSchema.optional(),
  proposedValue: JsonValueSchema.optional(),
});
export type FormField = Omit<z.infer<typeof FormFieldSchema>, 'type'> & {
  /** DataType — narrowed from z.string() to registry union */
  type: DataType;
};

// ─────────────────────────────────────────────────────────────
// HandlerPreview
// ─────────────────────────────────────────────────────────────

export const HandlerPreviewSchema = z.object({
  handlerLabel: z.string(),
  summary: z.string().optional(),
  destination: z.string().optional(),
  payloadPreview: z.record(z.string(), z.unknown()).optional(),
  payloadFieldCount: z.number().optional(),
});
export type HandlerPreview = z.infer<typeof HandlerPreviewSchema>;

// ─────────────────────────────────────────────────────────────
// ActionInteraction — discriminated union on `interaction`
// ─────────────────────────────────────────────────────────────

const ActionInteractionNoneSchema = z.object({
  interaction: z.literal('none'),
  /**
   * No UI to configure, with ONE exception: `onSuccess`.
   *
   * It is a post-landing instruction ("open the enroll queue"), not modal
   * chrome, so it outlives the modal it used to travel with — `approve_message`
   * is served as `none` on the queue destination and still has to open the queue
   * it just filled. Narrowed to that single key so this does not become a second
   * home for form config.
   */
  interactionConfig: z.object({ onSuccess: z.string() }).optional(),
});

const ActionInteractionConfirmSchema = z.object({
  interaction: z.literal('confirm'),
  interactionConfig: z.object({
    message: z.string().optional(),
    handlerPreview: HandlerPreviewSchema.optional(),
    confirmLabel: z.string().optional(),
  }).optional(),
});

/**
 * Per-action modal chrome, carried on every form-bearing interaction.
 *
 * MUST BE DECLARED HERE OR IT DOES NOT EXIST. Zod strips unknown keys, so a field
 * the builder puts on `interactionConfig` and this schema does not name is silently
 * deleted on the way to the client — the server sends it, validation removes it,
 * and the component sees `undefined` with nothing logged anywhere. That is exactly
 * how `submitLabel` and `onSuccess` failed silently: correct on the wire, absent in
 * the UI, and invisible to tsc on both sides.
 *
 * `submitLabel` — the drawer's submit copy, so an action can name what approving
 * actually does instead of every review action reading 'Approve & Push'.
 * `onSuccess`  — what the UI should open once the action lands (e.g.
 * `open_enroll_queue`), keeping action ids out of client conditionals.
 */
const MODAL_CHROME = {
  submitLabel: z.string().optional(),
  onSuccess: z.string().optional(),
};

const ActionInteractionModalSchema = z.object({
  interaction: z.literal('modal'),
  interactionConfig: z.object({
    form: z.record(z.string(), FormFieldSchema),
    ...MODAL_CHROME,
  }).optional(),
});

const ActionInteractionReviewSchema = z.object({
  interaction: z.literal('review'),
  interactionConfig: z.object({
    form: z.record(z.string(), FormFieldSchema),
    ...MODAL_CHROME,
  }).optional(),
});

/**
 * Carries SingleFormFieldSchema, used for forms with only one value per input field, as
 * opposed to forms that have a current and proposed value.
 */
const ActionInteractionReviewSingleSchema = z.object({
  interaction: z.literal('review_single'),
  interactionConfig: z.object({
    form: z.record(z.string(), SingleFormFieldSchema),
    ...MODAL_CHROME,
  }).optional(),
});

const ActionInteractionChatSchema = z.object({
  interaction: z.literal('chat'),
  interactionConfig: z.object({
    form: z.record(z.string(), FormFieldSchema),
  }).optional(),
});

export const ActionInteractionSchema = z.discriminatedUnion('interaction', [
  ActionInteractionNoneSchema,
  ActionInteractionConfirmSchema,
  ActionInteractionModalSchema,
  ActionInteractionReviewSchema,
  ActionInteractionReviewSingleSchema,
  ActionInteractionChatSchema,
]);

type WithNarrowedFormFields<Arm, Field> = Arm extends { interactionConfig?: infer Config }
  ? Omit<Arm, 'interactionConfig'> & {
    interactionConfig?: Omit<NonNullable<Config>, 'form'> & { form: Record<string, Field> };
  }
  : never;

export type ActionInteraction =
  | z.infer<typeof ActionInteractionNoneSchema>
  | z.infer<typeof ActionInteractionConfirmSchema>
  | WithNarrowedFormFields<z.infer<typeof ActionInteractionModalSchema>, FormField>
  | WithNarrowedFormFields<z.infer<typeof ActionInteractionReviewSchema>, FormField>
  | WithNarrowedFormFields<z.infer<typeof ActionInteractionReviewSingleSchema>, SingleFormField>
  | WithNarrowedFormFields<z.infer<typeof ActionInteractionChatSchema>, FormField>;

// ─────────────────────────────────────────────────────────────
// ViewAction
// ─────────────────────────────────────────────────────────────

const ViewActionBaseSchema = z.object({
  id: z.string(),
  label: z.string(),
  icon: z.string().optional(),
  isPrimary: z.boolean(),
  isTerminal: z.boolean().optional(),
  /** Repeatable action (Deal Brief `regenerate`): the client must neither hide it nor predict a conclude when clicked. */
  isRepeatable: z.boolean().optional(),
  supportsBulk: z.boolean().optional(),
  webhookDestination: z.string().optional(),
  /**
   * External destination for a link-style action — the button navigates instead of
   * dispatching an execution. Set from `Action.handlerConfig.url` (which supports
   * `{{dataKey}}` interpolation against artifact data), so a blueprint can add a
   * deep-link button without any new handler or interaction mode.
   *
   * Absent for every existing action, so nothing changes for them.
   */
  url: z.string().optional(),
});

export const ViewActionSchema = z.intersection(
  ViewActionBaseSchema,
  ActionInteractionSchema,
);

export type ViewAction = z.infer<typeof ViewActionBaseSchema> & ActionInteraction;

// ─────────────────────────────────────────────────────────────
// ViewItem
// ─────────────────────────────────────────────────────────────

export const ViewItemSchema = z.object({
  id: z.string(),
  kind: z.string().optional(),
  kindLabel: z.string().optional(),
  artifactCard: z.string(), // ArtifactCard — registry-derived

  // Display
  cardFields: z.array(DisplayFieldSchema),
  detailFields: z.array(DisplayFieldSchema),

  // State
  read: z.boolean(),
  outcome: z.object({
    status: z.string(),
    handlerSummary: z.string().optional(),
    executionError: z.object({
      code: z.string(),
      message: z.string(),
      executionRecordId: z.string().optional(),
      operationId: z.string().optional(),
      attemptCount: z.number().optional(),
      lastAttemptAt: z.string().optional(),
      timestamp: z.string().optional(),
    }).optional(),
  }).nullable(),
  deliveryStatus: z.enum(['pending', 'dispatching', 'delivered', 'failed']).optional(),

  // Actions
  actions: z.array(ViewActionSchema),
  completedActions: z.array(z.object({
    actionId: z.string(),
    label: z.string(),
    status: z.string(),
    deliveryStatus: z.string(),
  })),

  // Blueprint-derived union of all form-bearing action form keys, in
  // first-seen order. Sourced from blueprint.actions (NOT the runtime-
  // filtered actions[] above) so it bypasses the completed-action filter
  // — the skill Preview tab needs the schema, not the instance state.
  // Always present; populated as [] when no action declares a form.
  formFields: z.array(z.string()),

  // Filtering/grouping
  intent: z.string(), // Intent — registry-derived
  category: z.string(),
  accountId: z.string().optional(),
  accountName: z.string().optional(),
  accountFitScore: z.number().nullable().optional(),
  accountPropensityScore: z.number().nullable().optional(),
  opportunityAmount: z.number().nullable().optional(),
  opportunityId: z.string().nullable().optional(),
  opportunityCloseDate: z.string().nullable().optional(),
  opportunityNextStep: z.string().nullable().optional(),
  potentialContractValue: z.number().nullable().optional(),
  stage: z.string().optional(),
  date: z.string().optional(),
  correlationId: z.string().optional(),

  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ViewItem = Omit<z.infer<typeof ViewItemSchema>, 'intent' | 'artifactCard' | 'actions'> & {
  /** Narrowed from z.string() to registry union */
  artifactCard: ArtifactCard;
  intent: Intent;
  actions: ViewAction[];
};

// ─────────────────────────────────────────────────────────────
// FocusedArtifact — single-artifact response from
// GET /api/v4/artifacts/:id (used by useArtifact / FocusedArtifactPage).
// Same shape as ViewItem plus an optional `_adminContext` envelope when an
// admin is viewing another user's artifact.
// ─────────────────────────────────────────────────────────────

export const ArtifactAdminContextSchema = z.object({
  viewingOtherUser: z.boolean(),
  ownerId: z.number(),
  ownerEmail: z.string().nullable(),
  ownerSlackUserId: z.string().nullable(),
});
export type ArtifactAdminContext = z.infer<typeof ArtifactAdminContextSchema>;

export const FocusedArtifactSchema = ViewItemSchema.extend({
  _adminContext: ArtifactAdminContextSchema.optional(),
});

// ViewItem carries the registry-narrowed fields (artifactCard/intent/actions);
// the admin-context envelope is derived from FocusedArtifactSchema so it stays
// tied to the schema (satisfies terra/no-parallel-schema-and-type).
export type FocusedArtifact = ViewItem & {
  _adminContext?: z.infer<typeof FocusedArtifactSchema>['_adminContext'];
};

// ─────────────────────────────────────────────────────────────
// ViewBundle
// ─────────────────────────────────────────────────────────────

export const ViewBundleSchema = z.object({
  kind: z.string(),
  correlationId: z.string(),
  displayName: z.string(),
  items: z.array(ViewItemSchema),
  bulkActions: z.array(ViewActionSchema),
  bundleActions: z.array(ViewActionSchema).optional(),
  bundled: z.boolean(),
  groupPreset: z.string().optional(), // GroupPresetId — registry-derived
  stats: z.object({
    total: z.number(),
    pending: z.number(),
    actioned: z.number(),
    unread: z.number(),
  }),
  updatedAt: z.string(),
});

export type ViewBundle = Omit<z.infer<typeof ViewBundleSchema>, 'groupPreset' | 'items' | 'bulkActions' | 'bundleActions'> & {
  items: ViewItem[];
  bulkActions: ViewAction[];
  bundleActions?: ViewAction[];
  /** Narrowed from z.string() to registry union */
  groupPreset?: GroupPresetId | undefined;
};

// ─────────────────────────────────────────────────────────────
// ViewGroup
// ─────────────────────────────────────────────────────────────

export const ViewGroupSchema = z.object({
  key: z.string(),
  label: z.string(),
  count: z.number(),
  unreadCount: z.number(),
  context: z.string(), // ViewContext — registry-derived
});

export type ViewGroup = Omit<z.infer<typeof ViewGroupSchema>, 'context'> & {
  /** Narrowed from z.string() to registry union */
  context: ViewContext;
};

// ─────────────────────────────────────────────────────────────
// ViewControls
// ─────────────────────────────────────────────────────────────

export const ViewControlsUserSchema = z.object({
  id: z.number(),
  slackUserId: z.string(),
  ownerEmail: z.string().nullable(),
  userType: z.string(),
});
export type ViewControlsUser = z.infer<typeof ViewControlsUserSchema>;

export const ViewControlsSchema = z.object({
  authUser: ViewControlsUserSchema,
  actingUser: ViewControlsUserSchema,
  actingRoleSlugs: z.array(z.string()),
  authRoleSlugs: z.array(z.string()),
  delegation: z.object({
    id: z.number(),
    role: z.object({
      id: z.number(),
      slug: z.string(),
      name: z.string(),
      description: z.string().nullable(),
    }),
    delegatorId: z.number(),
    delegateId: z.number(),
    expiresAt: z.union([z.date(), z.string()]).nullable(),
    createdAt: z.union([z.date(), z.string()]),
    createdById: z.number().nullable(),
    notes: z.string().nullable(),
  }).optional(),
  isAdmin: z.literal(true).optional(),
  canImpersonate: z.literal(true).optional(),
  isManager: z.literal(true).optional(),
  salesforceConnectCta: z.object({ url: z.string() }).optional(),
});
export type ViewControls = z.infer<typeof ViewControlsSchema>;
